import apiClient from './api';
import { 
  SignInRequest, 
  SignInResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ConfirmResetCodeRequest,
  ConfirmResetCodeResponse,
  VerifyResetRequest,
  VerifyResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  Request2FAResponse,
  TwoFactorAuthRequest,
  TwoFactorAuthResponse,
  CreatePasswordRequest,
  CreatePasswordResponse,
  GetAllAdminsResponse,
  SuspendAdminResponse,
  ReactivateAdminResponse,
  AddAdminRequest,
  AddAdminResponse,
  EditAdminRequest,
  EditAdminResponse,
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPLoginRequest,
  VerifyOTPLoginResponse
} from '../types/auth.types';

export const authService = {
  /**
   * Admin login
   */
  signIn: async (credentials: SignInRequest): Promise<SignInResponse> => {
    const response = await apiClient.post<SignInResponse>(
      '/auth/admins/login',
      credentials
    );
    return response.data;
  },

  /**
   * Request password reset
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/admins/forgot-password',
      data
    );
    return response.data;
  },

  /**
   * Confirm reset code
   */
  confirmResetCode: async (data: ConfirmResetCodeRequest): Promise<ConfirmResetCodeResponse> => {
    const response = await apiClient.post<ConfirmResetCodeResponse>(
      '/auth/admins/confirm-reset-code',
      data
    );
    return response.data;
  },

  /**
   * Verify reset code (OTP for password reset)
   */
  verifyResetCode: async (data: VerifyResetRequest): Promise<VerifyResetResponse> => {
    const response = await apiClient.post<VerifyResetResponse>(
      '/auth/admin/verify-reset',
      data
    );
    return response.data;
  },

  /**
   * Reset password
   */
  resetPassword: async (data: ResetPasswordRequest, token: string): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post<ResetPasswordResponse>(
      '/auth/admins/reset-password',
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  /**
   * Request 2FA code (sends code to email)
   */
  request2FA: async (email: string): Promise<Request2FAResponse> => {
    const response = await apiClient.get<Request2FAResponse>(
      `/auth/admins/2fa?email=${encodeURIComponent(email)}`
    );
    return response.data;
  },

  /**
   * Verify two-factor authentication code
   */
  verifyTwoFactorAuth: async (data: TwoFactorAuthRequest): Promise<TwoFactorAuthResponse> => {
    const response = await apiClient.post<TwoFactorAuthResponse>(
      '/auth/admins/verify',
      data
    );
    return response.data;
  },

  /**
   * Send OTP to admin email (NEW OTP-only login)
   */
  sendOTP: async (data: SendOTPRequest): Promise<SendOTPResponse> => {
    const response = await apiClient.post<SendOTPResponse>(
      '/auth/admins/send-otp',
      data
    );
    return response.data;
  },

  /**
   * Verify OTP and login (NEW OTP-only login)
   */
  verifyOTPLogin: async (data: VerifyOTPLoginRequest): Promise<VerifyOTPLoginResponse> => {
    const response = await apiClient.post<VerifyOTPLoginResponse>(
      '/auth/admins/verify-otp',
      data
    );
    return response.data;
  },

  /**
   * Create password (first sign in)
   */
  createPassword: async (data: CreatePasswordRequest, token: string): Promise<CreatePasswordResponse> => {
    const response = await apiClient.post<CreatePasswordResponse>(
      '/auth/admins/set-password',
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },

  /**
   * Get all admins
   */
  getAllAdmins: async (): Promise<GetAllAdminsResponse> => {
    const response = await apiClient.get<GetAllAdminsResponse>(
      '/admins'
    );
    return response.data;
  },

  /**
   * Suspend admin
   */
  suspendAdmin: async (adminId: string): Promise<SuspendAdminResponse> => {
    const response = await apiClient.patch<SuspendAdminResponse>(
      `/${adminId}/suspend`
    );
    return response.data;
  },

  /**
   * Reactivate admin
   */
  reactivateAdmin: async (adminId: string): Promise<ReactivateAdminResponse> => {
    const response = await apiClient.patch<ReactivateAdminResponse>(
      `/${adminId}/reactivate`
    );
    return response.data;
  },

  /**
   * Add admin (send invitation)
   */
  addAdmin: async (data: AddAdminRequest): Promise<AddAdminResponse> => {
    const response = await apiClient.post<AddAdminResponse>(
      '/',
      data
    );
    return response.data;
  },

  /**
   * Edit admin
   */
  editAdmin: async (adminId: string, data: EditAdminRequest): Promise<EditAdminResponse> => {
    const response = await apiClient.patch<EditAdminResponse>(
      `/${adminId}`,
      data
    );
    return response.data;
  },

  /**
   * Logout
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/admins/logout');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
  },
};

